import React, { createContext, useContext } from 'react';
import { LiveListen, LiveListenCallData, useLiveListen } from 'shared/hooks/livelisten';
import { DialSessionStatusUpdates } from './DialSessionStatusUpdates';
import { useAppSelector } from 'shared/store';
import { useHasOrgPermission, useSocketSubscription } from 'shared/hooks';
import { selectActiveOrg } from 'shared/store/auth-slice';
import { PubsubChannelType } from 'sb-lib/types';
import { makeLog } from 'shared/utils/log';
import { LiveListenState } from 'types/livelisten';

const DEFAULT_LIVE_LISTEN = {
  disconnect: () => {},
  joinCall: () => {},
  audioRef: null,
  activeSessions: [] as LiveListenCallData[],
  joining: false,
  listenedSession: null,
};

const log = makeLog('LiveListenProvider');

const LiveListenDialSessionSubscription = () => {
  const activeOrg = useAppSelector(selectActiveOrg);
  log.info('Subscribing to DialSession creation/deletion updates via websockets.');

  // Listen to DialSessions starting and ending
  useSocketSubscription(
    PubsubChannelType['livelisten.dial_session.new'],
    `${activeOrg?.id}`,
    true,
  );

  useSocketSubscription(
    PubsubChannelType['livelisten.dial_session.ended'],
    `${activeOrg?.id}`,
    true,
  );
  return <></>;
};

export const LiveListenContext = createContext<LiveListen>(DEFAULT_LIVE_LISTEN);

export const LiveListenProvider: React.FC = ({ children }) => {
  const { autoListenState } = useAppSelector(s => s.livelisten);

  const livelisten = useLiveListen();
  const hasLiveListenPermission = useHasOrgPermission('liveListen', 'read');

  return (
    <LiveListenContext.Provider value={{ ...livelisten }}>
      {children}
      {hasLiveListenPermission && <LiveListenDialSessionSubscription />}
      <audio ref={livelisten.audioRef} autoPlay controls={false} />
      {autoListenState !== LiveListenState.OFF && (
        <>
          {livelisten.activeSessions.map(session => (
            <DialSessionStatusUpdates
              key={session.dialSession.id}
              dialSession={session.dialSession}
            />
          ))}
        </>
      )}
    </LiveListenContext.Provider>
  );
};

export const useLiveListenContext = () => {
  return useContext(LiveListenContext);
};
